import { Injectable, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { LocationRouteCffService, SentryLogger } from '@fcom/core/services';
import { Location } from '@fcom/core-api/interfaces';
import { finShare } from '@fcom/rx';
import { unsubscribe } from '@fcom/utils';

import { LocationAppState } from '../interfaces';
import { getLocation } from '../store/selectors';
import { LocationActions } from '../store/actions';

@Injectable({
  providedIn: 'root',
})
export class LocationsService implements OnDestroy {
  private subscriptions = new Subscription();

  constructor(
    private store$: Store<LocationAppState>,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private sentryLogger: SentryLogger
  ) {}

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  triggerLocationFetch(locationCode: string): void {
    this.subscriptions.add(
      this.languageService.locale
        .pipe(
          switchMap((locale) => this.locationRouteCffService.bestGuessFor(locationCode, locale)),
          takeUntil(this.locationAlreadyInStore(locationCode))
        )
        .subscribe({
          next: (l) => this.saveLocation(l),
          error: (error: unknown) => this.sentryLogger.error('Error fetching location info', { error, locationCode }),
        })
    );
  }

  retrieveLocation(key: string): Observable<Location> {
    return this.store$.select(getLocation(key));
  }

  saveLocation(destination: Location, oldDestination?: Location): void {
    if (oldDestination) {
      this.store$.dispatch(LocationActions.removeLocationData({ location: oldDestination }));
    }
    this.store$.dispatch(LocationActions.storeLocationData({ location: destination }));
  }

  private locationAlreadyInStore(locationCode: string): Observable<Location> {
    return this.retrieveLocation(locationCode).pipe(finShare(), filter<Location>(Boolean), take(1));
  }
}
